/*
 * Work Order Form
 *
 */

import * as React from 'react';

import { Button, Col, FormLabel, Form, Row } from 'react-bootstrap';
import {
    FieldConfig,
    FormArray,
    FormGenerator,
    FormGroup,
    GroupProps,
    Validators,
    AbstractControl
} from 'react-reactive-form';
import { IWorkOrder, IinstallBase, Ioption, WOClosingNote } from '../../models';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import {
    addWorkOrder,
    saveWorkOrderClosingNotes,
    saveWorkOrder,
    toggleEditPartModalForm,
    updateWorkOrder
} from '../../actions/workOrderActions';

import {
    jobTypesIdEnum,
    measurementPointResultStatusTypesEnum,
    workOrderStatusEnum,
    workOrderVendorsEnum
} from '../../models-enums';

import { FormUtil, RequiredIndicator } from '../common/FormUtil';
import { IworkOrderFormValues } from '../../modelsForms';
import { TFunction } from 'i18next';
import { constants } from '../../constants/constants';
import { initialWorkOrder } from '../../reducers/initialState';

import moment from 'moment';
import { getAssignedJobs } from '../../actions/manageJobActions';
import { ccWONotes } from '../../reducers/workOrderReducer';
import { setDecommissionAsset } from '../../actions/manageInventoryActions';
import { toastr } from 'react-redux-toastr';

interface Iprops {
    addWorkOrder: typeof addWorkOrder;
    saveWorkOrder: typeof saveWorkOrder;
    updateWorkOrder: typeof updateWorkOrder;
    saveWorkOrderClosingNotes: typeof saveWorkOrderClosingNotes;
    getJobs: typeof getAssignedJobs;
    loading: boolean;
    colorButton?: string;
    t: TFunction;
    selectedWorkOrder: IWorkOrder;
    standardOptions: Ioption[];
    selectedInstallBaseID: string;
    currentWorkOrder: IWorkOrder;
    shouldHideTechnicianSelect?: boolean;
    toggleEditPartModalForm: typeof toggleEditPartModalForm;
    disabled: boolean;
    /*
     * A past visit is on screen. The closing notes of a work order closed on another job are there
     * to be read: clicking one used to open it for editing, with Update Note and Delete Note
     * writing straight back to that closed work order (MMG-4345).
     */
    isHistoricalMode: boolean;
    jobTypeID: string;
    setDecommissionAsset: typeof setDecommissionAsset;
    selectedInstall: IinstallBase | undefined;
    decommissionAsset: boolean | undefined;
}

interface State {
    fieldConfig: FieldConfig;
    closingNotesFilled: boolean;
    newNote: string;
    showDecommissionConfirmModal: boolean;
    editingNote: WOClosingNote | null;
    isEditMode: boolean;
}

class WorkOrderForm extends React.Component<Iprops, State> {
    private formGroup: FormGroup | any;
    private subscription: any;
    private updateWorkOrderDebounced: (workOrder: Partial<IWorkOrder>) => void;
    static defaultProps = {
        colorButton: constants.colors[`blueButton`],
        selectedWorkOrder: initialWorkOrder
    };
    constructor(props: Iprops) {
        super(props);
        this.state = {
            fieldConfig: this.buildFieldConfig(),
            closingNotesFilled: false,
            newNote: '',
            showDecommissionConfirmModal: false,
            editingNote: null,
            isEditMode: false
        };
        this.updateWorkOrderDebounced = debounce(this.props.updateWorkOrder, constants.formDebounceTime);
    }

    componentDidMount() {
        // we don't need to do this because this form is updating the work order directly
        document.addEventListener('submitWorkOrderForms', this.handleSubmitCustomEvent, false);
    }

    componentDidUpdate(prevProps: Iprops, prevState: State) {
        if (
            prevProps.selectedWorkOrder.status !== this.props.selectedWorkOrder.status ||
            prevProps.isHistoricalMode !== this.props.isHistoricalMode
        ) {
            this.resetForm();
        }
    }

    resetForm = () => {
        this.setState({ fieldConfig: this.buildFieldConfig() });
    };

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        document.removeEventListener('updatedInstallBases', this.handleSubmitCustomEvent, false);
    }

    handleSubmitCustomEvent = () => {
        this.formGroup.handleSubmit();
    };

    notesListToOption = (notes: WOClosingNote[]) => {
        if (notes) {
            const fmtNotes = notes.map((note: WOClosingNote) => ({
                label: note.createDate,
                value: note.text
            }));

            return FormUtil.convertToOptions(fmtNotes);
        }

        return [];
    };

    /*
     * itemToFormValues - take the selectedItem and convert it to formValues
     */
    itemToFormValues = (): IworkOrderFormValues => {
        const { t } = this.props;
        const { vendor, status } = this.props.selectedWorkOrder;
        const selectedVendor = {
            value: vendor,
            label: t(workOrderVendorsEnum[vendor] || '')
        };
        const selectedStatus = {
            value: status,
            label: t(workOrderStatusEnum[status] || '')
        };
        const cleanedWorkOrder = omit(this.props.selectedWorkOrder, [
            'preventativeMaintenanceChecklist',
            'jobWorkOrders',
            'closingNotesList',
            'activeClosingNotes',
            'facility',
            'product',
            'parts'
        ]);
        const activeClosingNotes = this.props.selectedWorkOrder?.activeClosingNotes
            ? FormUtil.convertToSingleOption(this.props.selectedWorkOrder.activeClosingNotes)
            : '';

        return {
            ...cleanedWorkOrder,
            closingNotesList:
                // verify if closingNotesList is set, this was breaking in the next block
                this.props.currentWorkOrder && this.props.currentWorkOrder?.closingNotesList
                    ? this.notesListToOption(this.props.currentWorkOrder?.closingNotesList as WOClosingNote[])
                    : undefined,
            activeClosingNotes,
            vendor: selectedVendor,
            status: selectedStatus
        };
    };

    /*
     * formValuesToItem - convert the formValues to the shape of the selectedItem
     */
    formValuesToItem = (): IWorkOrder => {
        const cleanedWorkOrder = omit(this.props.selectedWorkOrder, [
            'preventativeMaintenanceChecklist',
            'jobWorkOrders'
        ]);
        const formValues = FormUtil.getValues(this.formGroup.value);

        return {
            ...cleanedWorkOrder,
            ...formValues,
            installBaseID: this.props.selectedWorkOrder.installBaseID || this.props.selectedInstallBaseID
        };
    };

    buildFieldConfig = (defaultValues: IworkOrderFormValues = this.itemToFormValues()) => {
        const disabled = this.props.disabled;
        const isComplete =
            defaultValues.status &&
            defaultValues.status.value === workOrderStatusEnum.complete &&
            defaultValues.status.value;

        let due_Date = defaultValues.dueDate as string | moment.Moment;
        // if the dueDate is not a moment and there is no id, we are creating an untouched new work order and default to a month from today

        if (typeof defaultValues.dueDate === 'undefined') {
            due_Date = moment.utc(defaultValues.dueDate);
        }

        const fieldConfigControls = {
            closingNotes: {
                render: FormUtil.TextInput,
                meta: {
                    label: '',
                    hideLabel: true,
                    colWidth: 12,
                    componentClass: 'textarea',
                    rows: 2,
                    name: 'closingNote',
                    initialContent: defaultValues.activeClosingNotes
                        ? (defaultValues.activeClosingNotes as Ioption).value
                        : !defaultValues.closingNotesList
                        ? defaultValues.closingNotes
                        : ''
                },
                formState: {
                    // 12/6/21 - ClosingNotes should no longer prepopulate with data, we might need to fix this though if
                    // it's required to enter notes and close without saving and coming back
                    value: '',
                    // Disable for completed work orders when not editing existing notes, and always in History
                    disabled: this.props.isHistoricalMode || (isComplete && !(this.state?.isEditMode ?? false))
                }
            },
            $field_2: {
                isStatic: false, // ensures a key is added
                // Saving, updating or deleting a note has nothing to offer a visit being viewed
                render: () =>
                    this.props.isHistoricalMode ? null : (
                    <Col
                        xs={12}
                        style={{
                            display: 'flex', // isComplete ? 'none' : 'flex' - Commented out to allow editing completed work orders
                            justifyContent: 'flex-end',
                            marginBottom: '1rem',
                            gap: '10px'
                        }}
                    >
                        {this.state.isEditMode && (
                            <Button type="button" variant="default" onClick={this.handleCancelEdit}>
                                Cancel
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant={this.props.colorButton || 'primary'}
                            disabled={(() => {
                                // Base requirement: must have content
                                if (!this.state.closingNotesFilled) return true;

                                // If work order is complete, check operation type
                                if (isComplete) {
                                    if (this.state.isEditMode) {
                                        // In edit mode: allow updates but prevent deletions
                                        const noteText = this.formGroup?.controls?.closingNotes?.value || '';
                                        return noteText.trim() === ''; // Disable if trying to delete (empty text)
                                    } else {
                                        // Not in edit mode: prevent adding new notes
                                        return true;
                                    }
                                }

                                return false; // Allow all operations if not complete
                            })()}
                            onClick={() => this.saveClosingNotes(!isComplete)}
                        >
                            {(() => {
                                if (this.state.isEditMode) {
                                    const noteText = this.formGroup?.controls?.closingNotes?.value?.trim() || '';
                                    if (noteText === '') {
                                        return 'Delete Note';
                                    } else {
                                        return 'Update Note';
                                    }
                                } else {
                                    return 'Save Notes';
                                }
                            })()}
                        </Button>
                    </Col>
                )
            },
            decommissionCheckbox: {
                render: ({ handler }: AbstractControl) => (
                    <Col xs={12} style={{ marginBottom: '1.5rem' }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start'
                            }}
                        >
                            <Form.Check
                                type="checkbox"
                                id="decommission-checkbox"
                                style={{ width: 'auto' }}
                                /*
                                 * This render builds its own input, so formState.disabled below never
                                 * reached it - the box stayed clickable in History and decommissioned
                                 * the asset on the open job (MMG-4345). Gated on History alone: the
                                 * container's `disabled` covers the work order fields, which this
                                 * checkbox is deliberately not one of.
                                 */
                                disabled={this.props.isHistoricalMode}
                                checked={handler().value}
                                onChange={(e: any) => {
                                    const checked = e.target.checked;
                                    const isDecommission = checked;
                                    const message = checked
                                        ? this.props.t('decommissionConfirm')
                                        : this.props.t('decommissionConfirmNot');

                                    const toastrConfirmOptions = {
                                        onOk: () => {
                                            // Update form state for visual feedback only
                                            handler().onChange(isDecommission);

                                            // Update install base decommission status
                                            this.props.setDecommissionAsset(
                                                this.props.selectedWorkOrder.installBaseID,
                                                isDecommission
                                            );
                                        },
                                        onCancel: () => {
                                            // Revert form state for visual feedback
                                            handler().onChange(!isDecommission);
                                        },
                                        okText: this.props.t('common:ok') || 'OK',
                                        cancelText: this.props.t('common:cancel') || 'Cancel'
                                    };

                                    toastr.confirm(message, toastrConfirmOptions);
                                }}
                            />
                            <div className="decommission-checkbox-wrapper">
                                <label className="decommission-label" htmlFor="decommission-checkbox">
                                    <strong>Decommission asset.</strong>
                                </label>
                                <small className="decommission-description">
                                    This will mark this asset as decommissioned when clicking Save & Log.
                                </small>
                            </div>
                        </div>
                    </Col>
                ),
                meta: {
                    label: 'Decommission',
                    colWidth: 12,
                    name: 'decommissionCheckbox'
                },
                formState: {
                    value:
                        this.props.decommissionAsset != null
                            ? this.props.decommissionAsset
                            : this.props.selectedInstall?.latestMeasurementPointListResultStatus ===
                              measurementPointResultStatusTypesEnum.resultStatusDecommissioned,
                    disabled
                }
            },
            number: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'woNumber',
                    colWidth: 3,
                    // autoFocus: true,
                    name: 'number'
                },
                formState: { value: defaultValues.number, disabled }
            },
            dueDate: {
                render: FormUtil.Datetime,
                meta: {
                    label: 'dueDate',
                    colWidth: 3,
                    showTime: false,
                    name: 'expiration-date'
                },
                formState: {
                    value: due_Date,
                    disabled
                },
                options: FormUtil.validators.requiredWithTrim
            },
            /*
             * vendor is disabled when on the beacon tab.  In other words the Vendor select is only enabled on the Repair tab.
             */
            vendor: {
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                },
                render: FormUtil.Select,
                meta: {
                    options: FormUtil.convertEnumToOptions(workOrderVendorsEnum),
                    label: 'vendor',
                    colWidth: 6,
                    name: 'vendor',
                    required: true,
                    isClearable: false,
                    shouldTranslate: true
                },
                formState: {
                    value: defaultValues.vendor,
                    disabled
                }
            },
            activityDescription: {
                render: FormUtil.TextInput,
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                },
                meta: {
                    label: 'activityDescription',
                    colWidth: 6,
                    name: 'notes',
                    componentClass: 'textarea',
                    rows: 5,
                    initialContent: defaultValues.activityDescription,
                    required: true
                },
                formState: {
                    value: defaultValues.activityDescription,
                    disabled
                }
            },

            contactInfo: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'contactinfo',
                    colWidth: 6,
                    name: 'contactInfo'
                },
                formState: { value: defaultValues.contactInfo, disabled },
                options: {
                    validators: Validators.pattern(
                        /^([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/ //eslint-disable-line
                    )
                }
            },

            status: {
                render: FormUtil.Select,
                meta: {
                    options: this.buildStatusOptions(),
                    label: 'status',
                    colWidth: 6,
                    name: 'status',
                    shouldTranslate: true
                },
                options: {
                    validators: [FormUtil.validators.requiredWithTrim]
                },
                formState: {
                    value: defaultValues.status,
                    disabled: this.shouldDisableStatusSelect()
                }
            },

            notes: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'notes',
                    colWidth: 6,
                    componentClass: 'textarea',
                    rows: 3,
                    name: 'notes',
                    initialContent: defaultValues.notes
                },
                formState: { value: defaultValues.notes, disabled }
            },
            technician: {
                render: FormUtil.TextInput,
                meta: {
                    label: 'tehnician',
                    colWidth: 6,
                    // autoFocus: true,
                    required: false,
                    name: 'tehnician'
                },
                formState: {
                    value: defaultValues.technician,
                    disabled
                }
            },
            $field_0: {
                isStatic: false, // ensures a key is added
                render: () => (
                    <Col
                        xs={6}
                        style={{
                            display: defaultValues.attachmentUrl1 ? '' : 'none'
                        }}
                    >
                        <Button variant={this.props.colorButton || 'primary'} href={`${defaultValues.attachmentUrl1}`}>
                            Download Document
                        </Button>
                    </Col>
                )
            }
        } as { [key: string]: GroupProps };

        // hide the status when creating new
        if (!(this.props.selectedWorkOrder && this.props.selectedWorkOrder.id.length)) {
            delete fieldConfigControls.status;
        }

        return FormUtil.translateForm(
            {
                controls: { ...fieldConfigControls }
            },
            this.props.t
        );
    };

    /*
     * status = completed: the only selectable options should be "reopened"
     * status = reopened: empty
     * status = new: empty
     */
    buildStatusOptions = () => {
        const { status } = this.props.selectedWorkOrder;
        if (status === workOrderStatusEnum.complete) {
            return [
                {
                    value: `${workOrderStatusEnum.reopened}`,
                    label: this.props.t('reopened')
                }
            ];
        } else {
            return [];
        }
    };

    /*
     * status select should only be enabled if creating new or if the WO is completed
     */
    shouldDisableStatusSelect = () => {
        if (this.props.disabled) {
            return true;
        }
        if (this.props.selectedWorkOrder.id.length === 0) {
            return false;
        } else if (this.props.selectedWorkOrder.status === workOrderStatusEnum.complete) {
            return false;
        } else {
            return true;
        }
    };

    /*
     * (reusable)
     * subscribe to the formGroup changes
     */
    subscribeToChanges = () => {
        for (const key in this.formGroup.controls) {
            if (this.formGroup.controls.hasOwnProperty(key)) {
                this.subscription = this.formGroup.get(key).valueChanges.subscribe((value: any) => {
                    if (value !== null) {
                        this.onValueChanges(value, key);
                    }
                });
            }
        }
    };

    /*
     * (reusable)
     * set the table filters to redux on each value change
     */
    onValueChanges = (value: any, key: string) => {
        if (key === 'closingNotes') {
            // Allow empty text when editing (for deletion), but require text when creating new notes
            const hasText = Boolean(value);
            const canSave = hasText || this.state.isEditMode;

            this.setState({
                closingNotesFilled: canSave
            });

            if (value) {
                const newNote = this.createClosingNoteItem(value);
                this.updateWorkOrderDebounced({
                    id: this.props.selectedWorkOrder.id,
                    activeClosingNotes: newNote
                });
            }
        } else if (key === 'decommissionCheckbox') {
            // Don't update work order for decommissionCheckbox - it only affects install base
            return;
        } else {
            this.updateWorkOrderDebounced({
                id: this.props.selectedWorkOrder.id,
                [key]: value
            });
        }
    };

    setForm = (form: FormGroup | FormArray) => {
        this.formGroup = form;
        this.formGroup.meta = {
            loading: this.props.loading
        };
        if (!this.subscription) {
            setTimeout(() => {
                this.subscribeToChanges();
            }, 300);
        }
    };

    cleanForm = () => {
        this.formGroup.reset();
    };

    createClosingNoteItem = (value: any): WOClosingNote => ({
        workOrderID: this.props.selectedWorkOrder.id,
        text: value,
        createDate: moment.utc().toISOString()
    });

    /*
     * Save & Log is disabled without closing notes for these job types (see PointListButtonBar),
     * so the field only carries the required marker when that gate actually applies.
     */
    closingNotesRequired = () => {
        const { jobTypeID } = this.props;
        return (
            jobTypeID === jobTypesIdEnum.repair ||
            jobTypeID === jobTypesIdEnum.maintenance ||
            jobTypeID === jobTypesIdEnum.warrantyBM ||
            jobTypeID === jobTypesIdEnum.servicePlan ||
            jobTypeID === jobTypesIdEnum.commissioning
        );
    };

    saveClosingNotes = (add: boolean) => {
        const noteText = this.formGroup.controls.closingNotes.value;

        // Check if work order is completed
        const defaultValues = this.itemToFormValues();
        const isComplete =
            defaultValues.status &&
            defaultValues.status.value === workOrderStatusEnum.complete &&
            defaultValues.status.value;

        if (this.state.isEditMode && this.state.editingNote) {
            // Handle update or delete - use the same saveWorkOrderClosingNotes action for everything
            const isEmptyText = !noteText || noteText.trim() === '';

            if (isEmptyText) {
                // Prevent deletion if work order is completed
                if (isComplete) {
                    return;
                }
                // For deletion, send empty text with existing createDate
                const noteToDelete: WOClosingNote = {
                    workOrderID: this.state.editingNote.workOrderID || this.props.selectedWorkOrder.id,
                    createDate: this.state.editingNote.createDate,
                    text: '' // Empty text signals deletion to backend
                };

                this.props.saveWorkOrderClosingNotes(this.props.selectedWorkOrder, noteToDelete);
            } else {
                // For update, send new text with existing createDate
                const updatedNote: WOClosingNote = {
                    workOrderID: this.state.editingNote.workOrderID || this.props.selectedWorkOrder.id,
                    createDate: this.state.editingNote.createDate,
                    text: noteText
                };

                this.props.saveWorkOrderClosingNotes(this.props.selectedWorkOrder, updatedNote);
            }

            // Exit edit mode
            this.setState({
                editingNote: null,
                isEditMode: false
            });
        } else {
            // Create new note (only allow if text is not empty and work order is not completed)
            if (noteText && noteText.trim() !== '') {
                // Prevent adding new notes if work order is completed
                if (isComplete) {
                    return;
                }

                const note: WOClosingNote = {
                    workOrderID: this.props.selectedWorkOrder.id,
                    createDate: moment.utc().toISOString(),
                    text: noteText
                };

                this.props.saveWorkOrderClosingNotes(this.props.selectedWorkOrder, note);
            }
        }

        // Clear the textarea
        this.formGroup.controls.closingNotes.setValue('');

        // Re-disable the control if work order is completed (after saving/exiting edit mode)
        if (isComplete) {
            this.formGroup.controls.closingNotes.disable();
        }

        this.setState({
            closingNotesFilled: false
        });
    };

    getClosingNotesList = (wo: IWorkOrder): WOClosingNote[] => {
        let notes = wo.closingNotesList || [];
        if (wo.closingNotes) {
            try {
                const potentialNotes = ccWONotes(JSON.parse(wo.closingNotes));
                potentialNotes.forEach(potentialNote => {
                    if (notes.find(n => n.createDate === potentialNote.createDate) === undefined) {
                        notes = notes.concat(potentialNote);
                    }
                });
            } catch (e) {
                console.error('error parsing closing notes', e);
            }
        }
        return notes;
    };

    handleNoteClick = (note: WOClosingNote) => {
        // Validate the note has required fields
        if (!note.createDate) {
            console.error('Note missing createDate:', note);
            return;
        }

        this.setState({
            editingNote: note,
            isEditMode: true
        });

        // Populate the textarea with the note text
        if (this.formGroup && this.formGroup.controls.closingNotes) {
            this.formGroup.controls.closingNotes.setValue(note.text);
            // Enable the control for editing (in case it was disabled for completed work orders)
            this.formGroup.controls.closingNotes.enable();
            // In edit mode, always allow saving (even for empty text to enable deletion)
            this.setState({
                closingNotesFilled: true
            });
        }
    };

    handleCancelEdit = () => {
        this.setState({
            editingNote: null,
            isEditMode: false
        });
        // Clear the textarea
        if (this.formGroup && this.formGroup.controls.closingNotes) {
            this.formGroup.controls.closingNotes.setValue('');

            // Re-disable the control if work order is completed (exiting edit mode)
            const defaultValues = this.itemToFormValues();
            const isComplete =
                defaultValues.status &&
                defaultValues.status.value === workOrderStatusEnum.complete &&
                defaultValues.status.value;

            if (isComplete) {
                this.formGroup.controls.closingNotes.disable();
            }

            this.setState({
                closingNotesFilled: false
            });
        }
    };

    render() {
        return (
            <div className='work-order-form-container'>
                <form
                    onSubmit={() => {
                        return;
                    }}
                    className="beacon-form work-order-form"
                >
                    {/*
                        Most efficient way to throw this dynamic list is outside the FormGenerator.
                        So to fake it being between the label and input, we're hiding the actual label and showing this one.
                    */}
                    <FormLabel className="wo-closing-notes-label" aria-hidden="true">
                        {String(this.props.t('closingNote'))}
                        <RequiredIndicator meta={{ required: this.closingNotesRequired() }} optionalText="" />
                    </FormLabel>
                    {(this.props.selectedWorkOrder.closingNotesList || this.props.selectedWorkOrder.closingNotes) && (
                        <ul className="wo-closing-notes" style={{ marginBottom: '10px' }}>
                            {this.getClosingNotesList(this.props.selectedWorkOrder).map((note, index) => {
                                const isBeingEdited =
                                    this.state.editingNote && note.createDate === this.state.editingNote.createDate;

                                // !isComplete was commented out to allow editing completed work orders, but a
                                // work order reached through History belongs to a closed job (MMG-4345)
                                const isInteractive = this.props.isHistoricalMode === false;

                                return (
                                    <li
                                        key={`${note.workOrderID}-${index}`}
                                        style={{
                                            cursor: isInteractive ? 'pointer' : 'default',
                                            backgroundColor: isBeingEdited ? '#f0f0f0' : 'transparent',
                                            color: isBeingEdited ? '#333' : '#666',
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            margin: '0',
                                            transition: isInteractive ? 'all 0.2s ease' : 'none',
                                            fontSize: '12.5px'
                                        }}
                                        onClick={isInteractive ? () => this.handleNoteClick(note) : undefined}
                                        onMouseEnter={
                                            isInteractive
                                                ? e => {
                                                      if (!isBeingEdited) {
                                                          e.currentTarget.style.textDecoration = 'underline';
                                                      }
                                                  }
                                                : undefined
                                        }
                                        onMouseLeave={
                                            isInteractive
                                                ? e => {
                                                      if (!isBeingEdited) {
                                                          e.currentTarget.style.textDecoration = 'none';
                                                      }
                                                  }
                                                : undefined
                                        }
                                    >
                                        <time dateTime={note?.createDate}>
                                            {moment(note?.createDate).format('YYYY-MM-DD')}
                                        </time>{' '}
                                        - {note?.text}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <Row>
                        <FormGenerator
                            onUnmount={this.cleanForm}
                            onMount={this.setForm}
                            fieldConfig={this.state.fieldConfig}
                        />
                    </Row>
                </form>
            </div>
        );
    }
}
export default WorkOrderForm;
